// Hook for managing the main invoice form state

import { useState, useCallback, useEffect, Dispatch, SetStateAction } from 'react';
import { Buyer, InvoiceItem, ShipToAddress, Seller } from '../../types/invoiceTypes';
import { DEFAULT_TAX_CATEGORY_SELECTION, type InvoiceTaxCategoryKey } from '../../utils/taxCategoryCatalog';
import { DownPayment } from '../../types/downPaymentTypes';
import { useAuth } from '../../context/AuthContext'; // Import useAuth to access sellerData
import { db } from '../../firebaseConfig'; // Import db for Firestore access
import { doc, getDoc } from 'firebase/firestore'; // Import Firestore functions
import { WorkflowService } from '../../services/workflowService'; // Import WorkflowService
import { getDownPaymentsByWorkflowId } from '../../api/downPaymentApi'; // Import API function
import { getInvoiceById } from '../../api/invoiceApi'; // Import getInvoiceById für Stornorechnungen
import logger from '../../utils/logger'; // Import logger
import { useTranslation } from 'react-i18next'; // Import useTranslation für Übersetzungen

// Define the state shape and the return type of the hook
export interface InvoiceFormState {
  isAdvancedMode: boolean;
  invoiceDate: string;
  currency: string;
  buyer: Buyer;
  items: InvoiceItem[];
  deliveryDate: string | null;
  useDifferentShipTo: boolean;
  shipToAddress: ShipToAddress;
  paymentTerms: string | null;
  notes: string | null;
  billingStartDate: string | null;
  billingEndDate: string | null;
  tenderOrLotReference: string | null;
  invoicedObjectId: string | null;
  invoicedObjectScheme: string | null;
  contractReference: string | null;
  projectReferenceId: string | null;
  projectReferenceName: string | null;
  paymentDueDate: string | null;
  paymentMandateReference: string | null;
  buyerOrderReference: string | null;
  sellerOrderReference: string | null;
  selectedOfferId: string | null;
  selectedOfferNumber: string;
  relatedOfferData: any | null; // Consider defining a more specific type
  offerWorkflowReferenceId: string | null;
  // Down payment states
  existingDownPayments: DownPayment[];
  hasDownPayments: boolean;
  totalDownPaymentAmount: number;
  remainingAmount: number;
  isLoadingDownPayments: boolean;
  showDownPaymentSection: boolean;
  // Inline down payment creation states
  inlineDownPaymentType: 'percentage' | 'amount';
  inlineDownPaymentValue: string;
  inlineDownPaymentDate: string;
  inlineDownPaymentNotes: string;
  isCreatingInlineDownPayment: boolean;
  // NEU: Mehrsprachigkeit - Dokumentsprache
  documentLanguage: string;
  // VB-1: rechnungsweiter Steuerart-Default für neue Positionen
  defaultTaxCategorySelection: InvoiceTaxCategoryKey;
}

// Define initial state structure (can be extracted for clarity)
const getInitialState = (sellerData?: Seller | null): InvoiceFormState => {
    // Bestimme Standard-Steuersatz
    let defaultTax = 19.0;
    if (sellerData?.isSmallBusiness) {
        defaultTax = 0.0;
    } else if (sellerData?.defaultTaxRate !== undefined && sellerData?.defaultTaxRate !== null) {
        defaultTax = sellerData.defaultTaxRate;
    }

    return {
    isAdvancedMode: false,
    invoiceDate: new Date().toISOString().split('T')[0],
    currency: 'EUR',
    buyer: {
      name: '',
      companyName: null,
      personName: null,
      street: '',
      zip: '',
      city: '',
      country: 'DE',
      id: null,
      street2: null,
      reference: null,
      communication: [],
      customerId: null,
    },
    items: [{
      id: Date.now().toString(),
      name: '',
      quantity: 1,
      netPrice: 0,
      unitPrice: 0,
      taxRate: defaultTax,
      unitCode: sellerData?.defaultUnitCode || 'H87', // Use seller default
    }],
    deliveryDate: null,
    useDifferentShipTo: false,
    shipToAddress: {
      name: '',
      street: '',
      street2: null,
      street3: null,
      zip: '',
      city: '',
      country: 'DE',
    },
    paymentTerms: sellerData?.defaultPaymentTerms || 'Zahlbar sofort ohne Abzug', // Use seller default
    notes: sellerData?.defaultNotes || 'Vielen Dank für Ihren Auftrag!', // Use seller default
    billingStartDate: null,
    billingEndDate: null,
    tenderOrLotReference: null,
    invoicedObjectId: null,
    invoicedObjectScheme: null,
    contractReference: null,
    projectReferenceId: null,
    projectReferenceName: null,
    paymentDueDate: null,
    paymentMandateReference: null,
    buyerOrderReference: null,
    sellerOrderReference: null,
    selectedOfferId: null,
    selectedOfferNumber: '',
    relatedOfferData: null,
    offerWorkflowReferenceId: null,
    // Down payment states
    existingDownPayments: [],
    hasDownPayments: false,
    totalDownPaymentAmount: 0,
    remainingAmount: 0,
    isLoadingDownPayments: false,
    showDownPaymentSection: false,
    // Inline down payment creation states
    inlineDownPaymentType: 'percentage' as const,
    inlineDownPaymentValue: '',
    inlineDownPaymentDate: new Date().toISOString().split('T')[0],
    inlineDownPaymentNotes: '',
    isCreatingInlineDownPayment: false,
    // NEU: Mehrsprachigkeit - Standard ist Deutsch
    documentLanguage: 'de',
    // VB-1: Standard-Steuerart ist „Standard" (19/7)
    defaultTaxCategorySelection: DEFAULT_TAX_CATEGORY_SELECTION,
};
};

export const useInvoiceForm = () => { // Removed initialState argument for now, using context
  const { sellerData, currentUser, effectiveScopeUid } = useAuth(); // Get sellerData and currentUser from context
  const { t } = useTranslation('invoice'); // Initialize translation
  
  // Initialize state using the function and sellerData
  const [state, setState] = useState<InvoiceFormState>(() => getInitialState(sellerData));
  const [offerSelectionError, setOfferSelectionError] = useState<string | null>(null); // State for offer selection errors

  // Update state when sellerData loads/changes to apply defaults
  useEffect(() => {
    // Verwende eine Verzögerung, damit das State-Update nicht während des Renderns passiert
    // sondern erst im nächsten Zyklus
    const timerId = setTimeout(() => {
      setState(prevState => {
        // Nur aktualisieren, wenn sich sellerData tatsächlich geändert hat
        // und nicht beim ersten Rendern
        const initialState = getInitialState(sellerData);
        
        // Behalte Benutzerdaten, aber aktualisiere Standardwerte
        return {
          ...initialState,
          // Diese Werte bleiben erhalten, wenn bereits gesetzt
          buyer: prevState.buyer?.name ? prevState.buyer : initialState.buyer,
          items: prevState.items.length > 0 && prevState.items[0]?.name ? 
                 prevState.items : initialState.items,
          // ⭐ WICHTIG: documentLanguage als Benutzerpräferenz beibehalten
          documentLanguage: prevState.documentLanguage,
        };
      });
    }, 0);
    
    // Cleanup-Funktion, um den Timer zu löschen, wenn die Komponente unmountet
    return () => clearTimeout(timerId);
  }, [sellerData]);

  // Define functions to update state
  const updateState = (newState: Partial<InvoiceFormState>) => {
    setState(prev => ({ ...prev, ...newState }));
  };

  // Function to reset the form to its initial state (based on current sellerData)
  const resetForm = useCallback(() => {
      logger.info("Resetting form with sellerData:", sellerData);
      setState(getInitialState(sellerData)); // Kompletter Reset inkl. Sprache auf 'de'
      setOfferSelectionError(null); // Also reset offer error
  }, [sellerData]);

  const setBuyer = (newBuyer: Buyer) => {
    setState(prev => ({ ...prev, buyer: newBuyer }));
  }

  // Update setItems to match Dispatch<SetStateAction<InvoiceItem[]>> type
  const setItems: Dispatch<SetStateAction<InvoiceItem[]>> = (value) => {
    setState(prev => {
        const newItems = typeof value === 'function' 
            ? (value as (prevState: InvoiceItem[]) => InvoiceItem[])(prev.items) 
            : value;
        return { ...prev, items: newItems };
    });
  };

  const setShipToAddress = (newAddress: ShipToAddress) => {
    setState(prev => ({ ...prev, shipToAddress: newAddress }));
  }

  // Add more specific setters as needed...
  const setIsAdvancedMode = (value: boolean) => updateState({ isAdvancedMode: value });
  const setInvoiceDate = (value: string) => updateState({ invoiceDate: value });
  const setCurrency = (value: string) => updateState({ currency: value });
  const setDeliveryDate = (value: string | null) => updateState({ deliveryDate: value });
  const setUseDifferentShipTo = (value: boolean) => updateState({ useDifferentShipTo: value });
  const setPaymentTerms = (value: string | null) => updateState({ paymentTerms: value });
  const setNotes = (value: string | null) => updateState({ notes: value });
  const setBillingStartDate = (value: string | null) => updateState({ billingStartDate: value });
  const setBillingEndDate = (value: string | null) => updateState({ billingEndDate: value });
  const setTenderOrLotReference = (value: string | null) => updateState({ tenderOrLotReference: value });
  const setInvoicedObjectId = (value: string | null) => updateState({ invoicedObjectId: value });
  const setInvoicedObjectScheme = (value: string | null) => updateState({ invoicedObjectScheme: value });
  const setContractReference = (value: string | null) => updateState({ contractReference: value });
  const setProjectReferenceId = (value: string | null) => updateState({ projectReferenceId: value });
  const setProjectReferenceName = (value: string | null) => updateState({ projectReferenceName: value });
  const setPaymentDueDate = (value: string | null) => updateState({ paymentDueDate: value });
  const setPaymentMandateReference = (value: string | null) => updateState({ paymentMandateReference: value });
  const setBuyerOrderReference = (value: string | null) => updateState({ buyerOrderReference: value });
  const setSellerOrderReference = (value: string | null) => updateState({ sellerOrderReference: value });
  const setSelectedOfferId = (value: string | null) => updateState({ selectedOfferId: value });
  const setSelectedOfferNumber = (value: string) => updateState({ selectedOfferNumber: value });
  const setRelatedOfferData = (value: any | null) => updateState({ relatedOfferData: value });
  const setOfferWorkflowReferenceId = (value: string | null) => updateState({ offerWorkflowReferenceId: value });
  
  // Down payment setters
  const setExistingDownPayments = (value: DownPayment[]) => updateState({ existingDownPayments: value });
  const setHasDownPayments = (value: boolean) => updateState({ hasDownPayments: value });
  const setTotalDownPaymentAmount = (value: number) => updateState({ totalDownPaymentAmount: value });
  const setRemainingAmount = (value: number) => updateState({ remainingAmount: value });
  const setIsLoadingDownPayments = (value: boolean) => updateState({ isLoadingDownPayments: value });
  const setShowDownPaymentSection = (value: boolean) => updateState({ showDownPaymentSection: value });
  
  // Inline down payment creation setters
  const setInlineDownPaymentType = (value: 'percentage' | 'amount') => updateState({ inlineDownPaymentType: value });
  const setInlineDownPaymentValue = (value: string) => updateState({ inlineDownPaymentValue: value });
  const setInlineDownPaymentDate = (value: string) => updateState({ inlineDownPaymentDate: value });
  const setInlineDownPaymentNotes = (value: string) => updateState({ inlineDownPaymentNotes: value });
  const setIsCreatingInlineDownPayment = (value: boolean) => updateState({ isCreatingInlineDownPayment: value });
  
  // NEU: Mehrsprachigkeit
  const setDocumentLanguage = (value: string) => updateState({ documentLanguage: value });

  // VB-1: Steuerart-Default
  const setDefaultTaxCategorySelection = (value: InvoiceTaxCategoryKey) =>
    updateState({ defaultTaxCategorySelection: value });

  // Handler für Inline-Abschlagsrechnung-Erstellung
  const handleInlineDownPaymentCreation = useCallback(async (format: 'zugferd' | 'xrechnung' = 'zugferd') => {
    if (!currentUser) {
      logger.error('Benutzer nicht authentifiziert');
      return { success: false, error: 'Benutzer nicht authentifiziert' };
    }

    // Validierung
    const downPaymentValue = parseFloat(state.inlineDownPaymentValue);
    if (!downPaymentValue || downPaymentValue <= 0) {
      return { success: false, error: 'Bitte geben Sie einen gültigen Wert ein' };
    }

    try {
      setIsCreatingInlineDownPayment(true);

      // Importiere API-Funktionen
      const { createDownPayment, calculateDownPaymentAmount, validateDownPaymentInput, downloadPdfBlob } = await import('../../api/downPaymentApi');

      // ⚠️ FIX: Berechne den GESAMTBETRAG für die Validierung
      // Die Validierung muss mit dem Gesamtbetrag arbeiten, nicht mit dem verbleibenden Betrag!
      // Sie zieht selbst die bereits gezahlten Abschläge ab.
      const totalExistingDownPayments = state.existingDownPayments.reduce((sum, dp) => sum + dp.amount, 0);
      const totalAmount = (state.remainingAmount || 0) + totalExistingDownPayments;
      
      // Validierung mit bestehenden Down Payments
      const validation = validateDownPaymentInput(
        totalAmount, // ✅ Verwende Gesamtbetrag statt verbleibendem Betrag
        state.existingDownPayments,
        state.inlineDownPaymentType,
        downPaymentValue
      );

      if (!validation.isValid) {
        return { success: false, error: validation.error || 'Ungültige Eingabe' };
      }

      // Erstelle Request
      const request = {
        baseDocumentId: state.selectedOfferId || 'current-invoice-draft',
        baseDocumentType: state.selectedOfferId ? 'offer' as const : 'invoice' as const,
        baseDocumentNumber: state.selectedOfferId ? state.selectedOfferNumber : undefined, // Benutzerfreundliche Angebotsnummer
        downPaymentType: state.inlineDownPaymentType,
        downPaymentValue: downPaymentValue,
        invoiceDate: state.inlineDownPaymentDate,
        notes: state.inlineDownPaymentNotes || undefined,
        format: format,
        // Vollständige Dokumentdaten für die e-Rechnung-Generierung
        seller: sellerData,
        buyer: state.buyer,
        items: state.items,
        currency: state.currency,
        deliveryDate: state.deliveryDate,
        paymentTerms: state.paymentTerms,
        dueDate: state.paymentDueDate,
        billingStartDate: state.billingStartDate,
        billingEndDate: state.billingEndDate,
        contractReference: state.contractReference,
        buyerOrderReference: state.buyerOrderReference,
        sellerOrderReference: state.sellerOrderReference,
        relatedOfferId: state.selectedOfferId,
        workflowReferenceId: state.offerWorkflowReferenceId,
        deliveryAddress: state.useDifferentShipTo ? state.shipToAddress : undefined,
        documentLanguage: state.documentLanguage || 'de' // NEU: Dokumentsprache für mehrsprachige Abschlagsrechnungen
      };

      const token = await currentUser.getIdToken();
      const result = await createDownPayment(request, token);
      
      downloadPdfBlob(result.blob, result.filename);

      // Berechne die tatsächliche Abschlagsrechnungssumme
      const calculatedAmount = calculateDownPaymentAmount(
        state.remainingAmount || 0,
        state.inlineDownPaymentType,
        downPaymentValue
      );

      // Erstelle neuen Down Payment für den State
      const newDownPayment = {
        id: 'temp_' + Date.now(),
        invoiceNumber: result.filename.replace('.pdf', '').replace('.xml', ''),
        baseDocumentId: request.baseDocumentId,
        baseDocumentType: request.baseDocumentType,
        downPaymentType: state.inlineDownPaymentType,
        downPaymentValue: downPaymentValue,
        amount: calculatedAmount,
        currency: state.currency,
        invoiceDate: state.inlineDownPaymentDate,
        status: 'issued' as const,
        notes: state.inlineDownPaymentNotes,
        workflowReferenceId: state.offerWorkflowReferenceId || undefined,
        createdAt: new Date().toISOString()
      };

      // Aktualisiere State
      const updatedDownPayments = [...state.existingDownPayments, newDownPayment];
      const newTotalDownPaymentAmount = updatedDownPayments.reduce((sum, dp) => sum + dp.amount, 0);
      
      updateState({
        existingDownPayments: updatedDownPayments,
        hasDownPayments: true,
        totalDownPaymentAmount: newTotalDownPaymentAmount,
        remainingAmount: Math.max(0, (state.remainingAmount || 0) - calculatedAmount),
        // Reset Inline-Form
        inlineDownPaymentType: 'percentage',
        inlineDownPaymentValue: '',
        inlineDownPaymentDate: new Date().toISOString().split('T')[0],
        inlineDownPaymentNotes: '',
        isCreatingInlineDownPayment: false
      });

      logger.info('Inline-Abschlagsrechnung erfolgreich erstellt:', newDownPayment);
      return { success: true, downPayment: newDownPayment };

    } catch (error) {
      logger.error('Fehler bei der Inline-Abschlagsrechnung-Erstellung:', error);
      setIsCreatingInlineDownPayment(false);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unbekannter Fehler bei der Abschlagsrechnung-Erstellung' 
      };
    }
  }, [currentUser, sellerData, state, updateState]);

  // Moved handleOfferSelection logic from InvoiceDashboard
  const handleOfferSelection = useCallback(async (offerId: string, offerNumber: string, offerData?: any) => {
      if (!currentUser) {
          logger.warn("handleOfferSelection called but no user logged in.");
          setOfferSelectionError("Bitte melden Sie sich an, um ein Angebot auszuwählen.");
          return;
      }

      setOfferSelectionError(null); // Clear previous error
      updateState({
          selectedOfferId: offerId,
          selectedOfferNumber: offerNumber,
          offerWorkflowReferenceId: null // Reset workflow reference initially
      });
      
      logger.info('Angebot ausgewählt:', { offerId, offerNumber });
      
      if (!offerId) {
          logger.info('Kein Angebot ausgewählt, Formular wird nicht geändert.');
          // Maybe reset fields here if desired? For now, just return.
          return;
      }
      
      try {
          const offerDocRef = doc(db, 'offers', offerId);
          const offerSnapshot = await getDoc(offerDocRef);
          
          let completeOfferData: any = {};
          
          if (offerSnapshot.exists()) {
              const firestoreData = offerSnapshot.data();
              logger.info('Firestore offer data found:', firestoreData);
              
              if (firestoreData.rawData) {
                  logger.info('Using rawData from Firestore');
                  completeOfferData = { ...firestoreData.rawData };
                  if (!completeOfferData.buyer && firestoreData.buyer) {
                      completeOfferData.buyer = firestoreData.buyer;
                  }
                  // Ensure items is an array
                  if (completeOfferData.items && typeof completeOfferData.items === 'string') {
                      try { completeOfferData.items = JSON.parse(completeOfferData.items); } catch (e) { logger.error('Failed to parse items JSON', e); completeOfferData.items = []; }
                  } else if (!Array.isArray(completeOfferData.items)) {
                       logger.warn('rawData.items is not an array, setting to empty.');
                       completeOfferData.items = [];
                  }
              } else {
                   logger.info('No rawData found, attempting reconstruction');
                  completeOfferData = { ...firestoreData }; // Start with main Firestore data
                   // Try to reconstruct buyer if not present
                  if (!completeOfferData.buyer && firestoreData.buyerName) {
                      completeOfferData.buyer = {
                          name: firestoreData.buyerName,
                          street: firestoreData.buyerStreet || '',
                          zip: firestoreData.buyerZip || '',
                          city: firestoreData.buyerCity || '',
                          country: firestoreData.buyerCountry || 'DE',
                          // Add other buyer fields if available in firestoreData
                      };
                  }
                   // Ensure items is an array, potentially create synthetic items
                  if (!Array.isArray(completeOfferData.items)) {
                       logger.info('No valid items array found in Firestore main data, creating synthetic.');
                       completeOfferData.items = [{ // Create a default item if none found
                          id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
                          name: `Position aus Angebot ${offerNumber}`,
                          quantity: 1,
                          netPrice: firestoreData.totalAmount || 0,
                          unitPrice: firestoreData.totalAmount || 0,
                          taxRate: 19.0,
                          description: 'Details aus Angebot nicht verfügbar',
                          unitCode: state.items[0]?.unitCode || 'H87' // Use existing default if possible
                      }];
                  }
              }
               // Ensure necessary fields have defaults
              completeOfferData.currency = completeOfferData.currency || firestoreData.currency || 'EUR';
              completeOfferData.offerNumber = completeOfferData.offerNumber || firestoreData.offerNumber || offerNumber;
              completeOfferData.workflowReferenceId = completeOfferData.workflowReferenceId || firestoreData.workflowReferenceId; // Get workflow ref from Firestore
          
          } else if (offerData) {
               logger.info('Offer not found in Firestore, using provided offerData');
              completeOfferData = { ...offerData }; // Fallback to passed data
              if (!Array.isArray(completeOfferData.items)) { // Ensure items is an array
                   logger.warn('Provided offerData.items is not an array, creating synthetic.');
                   completeOfferData.items = [{ // Create a default item
                      id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
                      name: 'Position aus Angebot (Details unbekannt)',
                      quantity: 1, netPrice: 0, unitPrice: 0, taxRate: 19.0,
                      description: 'Bitte manuell prüfen', unitCode: state.items[0]?.unitCode || 'H87'
                  }];
              }
          } else {
              throw new Error('Keine Angebotsdaten gefunden (weder Firestore noch übergeben)');
          }

          // Debugge die übergebenen Daten vor der Verarbeitung
          logger.info('Final offer data structure before processing:', {
            hasItems: !!completeOfferData.items && Array.isArray(completeOfferData.items),
            itemsLength: Array.isArray(completeOfferData.items) ? completeOfferData.items.length : 0,
            hasBuyer: !!completeOfferData.buyer && typeof completeOfferData.buyer === 'object',
            currency: completeOfferData.currency,
            offerNumber: completeOfferData.offerNumber,
            workflowRef: completeOfferData.workflowReferenceId,
          });

          if (Array.isArray(completeOfferData.items) && completeOfferData.items.length > 0) {
            // Prüfe das erste Item, ob es die erforderlichen Felder hat
            const firstItem = completeOfferData.items[0];
            logger.info('First item structure check:', {
                hasId: !!firstItem.id,
                hasName: !!firstItem.name,
                hasQuantity: typeof firstItem.quantity === 'number',
                hasNetPrice: typeof firstItem.netPrice === 'number',
                hasUnitPrice: typeof firstItem.unitPrice === 'number',
                netPriceValue: firstItem.netPrice,
                unitPriceValue: firstItem.unitPrice,
                taxRateValue: firstItem.taxRate
            });
          }

          // Prepare state updates
          const updates: Partial<InvoiceFormState> = {
               relatedOfferData: completeOfferData, // Store the raw data used
          };

          if (completeOfferData.buyer && typeof completeOfferData.buyer === 'object') {
               logger.info('Updating buyer data:', completeOfferData.buyer);
              
              // Verbesserte Buyer-Logik: Extrahiere und validiere Felder explizit
              const updatedBuyer = { ...state.buyer };
              
              // Stelle sicher, dass name immer gesetzt ist
              if (completeOfferData.buyer.name) {
                updatedBuyer.name = completeOfferData.buyer.name;
              }
              
              // Stelle sicher, dass personName korrekt gesetzt ist
              if (completeOfferData.buyer.personName) {
                updatedBuyer.personName = completeOfferData.buyer.personName;
              } else if (completeOfferData.buyer.buyerContactName) {
                // Alternative Feldnamen berücksichtigen
                updatedBuyer.personName = completeOfferData.buyer.buyerContactName;
              }
              
              // Stelle sicher, dass companyName korrekt gesetzt ist
              if (completeOfferData.buyer.companyName) {
                updatedBuyer.companyName = completeOfferData.buyer.companyName;
              } 
              
              // Wenn name existiert, aber weder personName noch companyName, leite ab
              if (updatedBuyer.name && (!updatedBuyer.personName && !updatedBuyer.companyName)) {
                // Wenn name existiert aber companyName und personName fehlen, 
                // setze personName auf name als Fallback
                updatedBuyer.personName = updatedBuyer.name;
              }

              // Weitere Felder übertragen, falls vorhanden
              if (completeOfferData.buyer.street) updatedBuyer.street = completeOfferData.buyer.street;
              if (completeOfferData.buyer.zip) updatedBuyer.zip = completeOfferData.buyer.zip;
              if (completeOfferData.buyer.city) updatedBuyer.city = completeOfferData.buyer.city;
              if (completeOfferData.buyer.country) updatedBuyer.country = completeOfferData.buyer.country;
              if (completeOfferData.buyer.street2) updatedBuyer.street2 = completeOfferData.buyer.street2;
              if (completeOfferData.buyer.reference) updatedBuyer.reference = completeOfferData.buyer.reference;
              if (completeOfferData.buyer.customerId) updatedBuyer.customerId = completeOfferData.buyer.customerId;
              if (completeOfferData.buyer.id) updatedBuyer.id = completeOfferData.buyer.id;
              if (completeOfferData.buyer.email) updatedBuyer.email = completeOfferData.buyer.email;
              if (completeOfferData.buyer.phone) updatedBuyer.phone = completeOfferData.buyer.phone;
              
              // Debug-Ausgabe für die Felder
              logger.debug('Buyer Felder nach Update:', {
                name: updatedBuyer.name,
                personName: updatedBuyer.personName,
                companyName: updatedBuyer.companyName
              });
              
              // Aktualisiere den buyer in updates
              updates.buyer = updatedBuyer;
          } else if (completeOfferData.buyerName) {
               logger.info('Only buyerName found, updating name:', completeOfferData.buyerName);
              // Wenn nur buyerName vorhanden ist, erstelle ein einfaches Buyer-Objekt
              updates.buyer = { 
                ...state.buyer, 
                name: completeOfferData.buyerName,
                personName: completeOfferData.buyerName // Setze personName auch auf buyerName als Fallback
              };
          }

          if (completeOfferData.items && Array.isArray(completeOfferData.items) && completeOfferData.items.length > 0) {
              logger.info('Validating and updating items:', completeOfferData.items.length);
              
              // Füge Debug-Ausgabe hinzu, um die Item-Struktur zu verstehen
              logger.debug('Example item data structure:', completeOfferData.items[0]);
              
              const validatedItems = completeOfferData.items.map((item: any, index: number) => {
                  const itemId = item.id || Date.now().toString() + Math.random().toString(36).substring(2, 9) + index;
                  
                  // Debug-Ausgaben zur besseren Fehlersuche
                  logger.debug(`Processing item ${index} with name: "${item.name}", netPrice: ${item.netPrice}, unitPrice: ${item.unitPrice}`);
                  
                  // Sichere Extrahierung des Namens mit Fallback
                  const itemName = typeof item.name === 'string' ? item.name.trim() : `Position ${index + 1}`;
                  
                  // Sichere Extrahierung der Preise mit besserer Typprüfung
                  // Priorisiere netPrice, wenn vorhanden und gültig
                  let itemNetPrice = 0;
                  let itemUnitPrice = 0;
                  let itemGrossPrice = 0;
                  
                  // Prüfe erst explizit netPrice
                  if (typeof item.netPrice === 'number' && !isNaN(item.netPrice)) {
                      itemNetPrice = item.netPrice;
                      // Berechne unitPrice falls nicht vorhanden
                      itemUnitPrice = typeof item.unitPrice === 'number' && !isNaN(item.unitPrice) ? 
                          item.unitPrice : itemNetPrice;
                      // Berechne grossPrice basierend auf netPrice und taxRate
                      itemGrossPrice = itemNetPrice * (1 + (typeof item.taxRate === 'number' && !isNaN(item.taxRate) ? item.taxRate : 19.0) / 100);
                  } 
                  // Fallback auf unitPrice wenn netPrice nicht vorhanden
                  else if (typeof item.unitPrice === 'number' && !isNaN(item.unitPrice)) {
                      itemUnitPrice = item.unitPrice;
                      itemNetPrice = itemUnitPrice;
                      // Berechne grossPrice basierend auf netPrice und taxRate
                      itemGrossPrice = itemNetPrice * (1 + (typeof item.taxRate === 'number' && !isNaN(item.taxRate) ? item.taxRate : 19.0) / 100);
                  }
                  
                  // Logge die finalen berechneten Werte
                  logger.debug(`Final values for item ${index}: name="${itemName}", netPrice=${itemNetPrice}, unitPrice=${itemUnitPrice}, grossPrice=${itemGrossPrice}`);
                  
                  return {
                      id: itemId,
                      name: itemName,
                      quantity: typeof item.quantity === 'number' && !isNaN(item.quantity) ? item.quantity : 1,
                      // Die verbesserten Preiswerte nutzen
                      netPrice: itemNetPrice,
                      unitPrice: itemUnitPrice,
                      grossPrice: itemGrossPrice, // Stelle sicher, dass grossPrice gesetzt ist
                      taxRate: typeof item.taxRate === 'number' && !isNaN(item.taxRate) ? item.taxRate : 19.0,
                      description: item.description || '',
                      unitCode: item.unitCode || state.items[0]?.unitCode || 'H87', // Use existing default
                      // Füge alle anderen Felder hinzu, die im Original vorhanden waren
                      sellerArticleId: item.sellerArticleId || null,
                      buyerArticleId: item.buyerArticleId || null,
                      globalId: item.globalId || null, 
                      globalIdScheme: item.globalIdScheme || null
                  };
              });
              updates.items = validatedItems;
          } else {
               logger.warn('No valid items found in offer data, keeping existing items.');
              // Optionally reset items to default here if required
              // updates.items = getInitialState(sellerData).items; 
          }

          // Apply other relevant fields from offer data
          if (completeOfferData.currency) updates.currency = completeOfferData.currency;
          if (completeOfferData.notes) updates.notes = completeOfferData.notes;
          if (completeOfferData.paymentTerms) updates.paymentTerms = completeOfferData.paymentTerms;
          if (completeOfferData.contractReference) updates.contractReference = completeOfferData.contractReference;
          if (completeOfferData.projectReferenceId) updates.projectReferenceId = completeOfferData.projectReferenceId;
          if (completeOfferData.projectReferenceName) updates.projectReferenceName = completeOfferData.projectReferenceName;
          if (completeOfferData.deliveryDate) updates.deliveryDate = completeOfferData.deliveryDate;
          if (completeOfferData.buyerOrderReference) updates.buyerOrderReference = completeOfferData.buyerOrderReference;

          if (completeOfferData.shipTo) {
              updates.shipToAddress = completeOfferData.shipTo;
              updates.useDifferentShipTo = true; // Assume if shipTo is provided, it should be used
          }

          // ⭐ NEU: Dokumentsprache vom Angebot übernehmen
          if (completeOfferData.documentLanguage) {
              updates.documentLanguage = completeOfferData.documentLanguage;
              logger.info('Dokumentsprache vom Angebot übernommen:', completeOfferData.documentLanguage);
          }

          // Find and apply workflow reference *after* getting Firestore data
          if (offerSnapshot.exists() && currentUser) {
               try {
                  const workflowStages = await WorkflowService.findStagesByDocumentNumber(
                      effectiveScopeUid,
                      offerNumber // Use the selected offerNumber
                  );
                  if (workflowStages.length > 0) {
                      updates.offerWorkflowReferenceId = workflowStages[0].referenceId;
                      logger.info('Workflow reference found and applied:', workflowStages[0].referenceId);
                      
                      // Load existing down payments for this workflow
                      try {
                          updates.isLoadingDownPayments = true;
                          const token = await currentUser.getIdToken();
                          const downPayments = await getDownPaymentsByWorkflowId(workflowStages[0].referenceId, token);
                          
                          const totalDownPaymentAmount = downPayments.reduce((sum, dp) => sum + dp.amount, 0);
                          
                          // Calculate remaining amount based on current items (not stored totalAmount)
                          const currentItems = updates.items || state.items;
                          const currentTotal = currentItems.reduce((sum: number, item: any) => {
                              const netPrice = item.netPrice * item.quantity;
                              const taxAmount = netPrice * (item.taxRate / 100);
                              return sum + netPrice + taxAmount;
                          }, 0);
                          const remainingAmount = Math.max(0, currentTotal - totalDownPaymentAmount);
                          
                          updates.existingDownPayments = downPayments;
                          updates.hasDownPayments = downPayments.length > 0;
                          updates.totalDownPaymentAmount = totalDownPaymentAmount;
                          updates.remainingAmount = remainingAmount;
                          updates.isLoadingDownPayments = false;
                          
                          logger.info('Down payments loaded successfully:', {
                              count: downPayments.length,
                              totalAmount: totalDownPaymentAmount,
                              remainingAmount: remainingAmount
                          });
                      } catch (dpError) {
                          logger.error('Error loading down payments:', dpError);
                          updates.existingDownPayments = [];
                          updates.hasDownPayments = false;
                          updates.totalDownPaymentAmount = 0;
                          updates.remainingAmount = 0;
                          updates.isLoadingDownPayments = false;
                      }
                  } else {
                       logger.info('No workflow reference found for this offer.');
                      updates.offerWorkflowReferenceId = null; // Ensure it's reset if not found
                      // Reset down payment states when no workflow found
                      updates.existingDownPayments = [];
                      updates.hasDownPayments = false;
                      updates.totalDownPaymentAmount = 0;
                      updates.remainingAmount = 0;
                      updates.isLoadingDownPayments = false;
                  }
               } catch (wfError) {
                   logger.error('Error finding workflow stages:', wfError);
                  // Don't block update due to workflow error, just log it
                  // Reset down payment states on error
                  updates.existingDownPayments = [];
                  updates.hasDownPayments = false;
                  updates.totalDownPaymentAmount = 0;
                  updates.remainingAmount = 0;
                  updates.isLoadingDownPayments = false;
               }
          } else {
              // Reset workflow ref if no Firestore data or user
              updates.offerWorkflowReferenceId = null; 
              // Reset down payment states when no workflow data available
              updates.existingDownPayments = [];
              updates.hasDownPayments = false;
              updates.totalDownPaymentAmount = 0;
              updates.remainingAmount = 0;
              updates.isLoadingDownPayments = false;
          }

          // Apply all collected updates to the state
          updateState(updates);
          logger.info('Offer data applied successfully.');

      } catch (err) {
           logger.error('Error processing offer selection:', err);
          setOfferSelectionError(`Fehler beim Laden der Angebotsdaten: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`);
          // Optionally reset parts of the form on error?
          // updateState({ items: getInitialState(sellerData).items });
      }
  }, [currentUser, sellerData, state.buyer, state.items, updateState]); // Dependencies: Need buyer/items from state for defaults/merging

  /**
   * Lädt eine bestehende Rechnung und bereitet die Daten für eine Stornorechnung vor
   * @param invoiceId Die Firestore-ID der zu stornierenden Rechnung
   * @param invoiceNumber Die Rechnungsnummer der zu stornierenden Rechnung
   */
  const loadCancellationInvoiceData = useCallback(async (invoiceId: string, invoiceNumber: string) => {
      if (!currentUser) {
          logger.error('Cannot load cancellation data: No user logged in');
          throw new Error('Nicht angemeldet');
      }
      
      try {
          logger.info(`Loading invoice for cancellation: ${invoiceId}`);
          const token = await currentUser.getIdToken();
          const invoiceData = await getInvoiceById(invoiceId, token);
          
          logger.info('Loaded invoice for cancellation:', invoiceData);
          
          // Sicherheitscheck: Prüfe, ob die Rechnung bereits storniert wurde
          if (invoiceData.isCancelled) {
              throw new Error(t('cancellation.errors.alreadyCancelled', 'Diese Rechnung wurde bereits storniert.'));
          }
          
          // Sicherheitscheck: Prüfe, ob die Rechnung selbst eine Stornorechnung ist
          if (invoiceData.isCancellation) {
              throw new Error(t('cancellation.errors.cannotCancelCancellation', 'Stornorechnungen können nicht erneut storniert werden.'));
          }
          
          // ⭐ NEU: Speichere Originalrechnungsdaten für Stornorechnung
          const originalInvoiceDateRaw = invoiceData.invoiceDate;
          // Intelligenter Fallback: gespeichertes Leistungsdatum → Rechnungsdatum des Originals → null.
          // Leerstring zählt wie fehlend ('||'-Semantik gewollt).
          const cancellationDeliveryDate = invoiceData.deliveryDate || invoiceData.invoiceDate || null;
          if (!invoiceData.deliveryDate && invoiceData.invoiceDate) {
              logger.info('[loadCancellationInvoiceData] deliveryDate fehlt — Fallback auf invoiceDate:', invoiceData.invoiceDate);
          }
          
          // Formatiere das Datum von YYYY-MM-DD zu DD.MM.YYYY
          let formattedOriginalDate = t('cancellation.unknownDate', 'unbekannt');
          if (originalInvoiceDateRaw) {
              try {
                  const dateParts = originalInvoiceDateRaw.split('-');
                  if (dateParts.length === 3) {
                      formattedOriginalDate = `${dateParts[2]}.${dateParts[1]}.${dateParts[0]}`;
                  } else {
                      formattedOriginalDate = originalInvoiceDateRaw;
                  }
              } catch (e) {
                  formattedOriginalDate = originalInvoiceDateRaw;
              }
          }
          
          // Bereite erweiterte Notiz vor (mit Datum der Originalrechnung) - OHNE Originalnotiz
          const cancellationNote = t('cancellation.noteTemplate', { invoiceNumber: invoiceNumber }) + 
              ` ${t('cancellation.from', 'vom')} ${formattedOriginalDate}`;
          
          // Alle Positionen vorzeichenumgekehrt vorbereiten.
          //
          // ECHTE NEGATION, NICHT `-Math.abs()` (Fix 2026-08-07, Folge-Ticket aus
          // Review-Report #327):
          // `-Math.abs(x)` erzwingt IMMER ein negatives Ergebnis. Das ist nur für
          // durchweg positive Originalpositionen richtig. Enthält die Originalrechnung
          // eine bereits negative Position — Rabatt-/Gutschriftzeile, oder das Storno
          // einer bereits korrigierten Rechnung — dann wurde diese Position im Storno
          // ein zweites Mal negativ statt positiv, und die Stornorechnung hob die
          // Originalrechnung nicht mehr auf.
          //
          // Beispiel (Original: Leistung 100,00 €, Rabattzeile −20,00 €, Summe 80,00 €):
          //   `-Math.abs()`: Storno −100,00 € und −20,00 € ⇒ Summe −120,00 €
          //                  Original + Storno = −40,00 €  ✗ Restsaldo bleibt stehen
          //   echte Negation: Storno −100,00 € und +20,00 € ⇒ Summe −80,00 €
          //                  Original + Storno = 0,00 €      ✓
          //
          // Die Storno muss die ursprüngliche Entgelt- und Steuerschuld exakt
          // neutralisieren (§ 17 UStG); ein verbleibender Saldo ist ein echter
          // Buchungsfehler, kein Darstellungsdetail.
          //
          // `|| 0` / `?? `-Semantik bleibt unverändert — geändert ist ausschließlich,
          // dass negiert statt „negativ gemacht" wird.
          const negativeItems: InvoiceItem[] = (invoiceData.items || []).map((item: any) => ({
              ...item,
              id: Date.now().toString() + Math.random().toString(), // Neue temporäre ID
              netPrice: -(item.netPrice || 0),
              unitPrice: -(item.unitPrice ?? item.netPrice ?? 0), // Fallback auf netPrice für API-Rechnungen
              grossPrice: item.grossPrice ? -item.grossPrice : undefined
          }));
          
          // Parse buyer data - handle different formats
          let buyerData = invoiceData.buyer;
          if (typeof invoiceData.buyer === 'string') {
              try {
                  buyerData = JSON.parse(invoiceData.buyer);
              } catch (e) {
                  logger.warn('Could not parse buyer data, using as string');
                  buyerData = { name: invoiceData.buyer, street: '', zip: '', city: '', country: 'DE' };
              }
          }

          // Normalisiere buyer.name — API-Rechnungen haben companyName/personName, aber kein name-Feld
          if (buyerData && typeof buyerData === 'object' && !buyerData.name) {
              buyerData = {
                  ...buyerData,
                  name: buyerData.companyName || buyerData.personName || '',
              };
          }
          
          // Setze alle Formularfelder
          updateState({
              invoiceDate: new Date().toISOString().split('T')[0], // Heutiges Datum
              currency: invoiceData.currency || 'EUR',
              buyer: buyerData || state.buyer,
              items: negativeItems,
              deliveryDate: cancellationDeliveryDate, // ⭐ Fallback: deliveryDate → invoiceDate des Originals → null
              useDifferentShipTo: !!invoiceData.shipTo,
              shipToAddress: invoiceData.shipTo || state.shipToAddress,
              paymentTerms: invoiceData.paymentTerms || state.paymentTerms,
              notes: cancellationNote, // ⭐ Nur Stornonotiz, OHNE Originalnotiz der ursprünglichen Rechnung
              billingStartDate: invoiceData.billingStartDate || null,
              billingEndDate: invoiceData.billingEndDate || null,
              tenderOrLotReference: invoiceData.tenderOrLotReference || null,
              invoicedObjectId: invoiceData.invoicedObjectId || null,
              invoicedObjectScheme: invoiceData.invoicedObjectScheme || null,
              contractReference: invoiceData.contractReference || null,
              projectReferenceId: invoiceData.projectReferenceId || null,
              projectReferenceName: invoiceData.projectReferenceName || null,
              paymentDueDate: invoiceData.paymentDueDate || null,
              paymentMandateReference: invoiceData.paymentMandateReference || null,
              buyerOrderReference: invoiceData.buyerOrderReference || null,
              sellerOrderReference: invoiceData.sellerOrderReference || null,
              // ⭐ WICHTIG: Workflow-Verknüpfung beibehalten
              offerWorkflowReferenceId: invoiceData.workflowReferenceId || null,
              // ⭐ NEU: Dokumentsprache der Originalrechnung übernehmen
              documentLanguage: invoiceData.documentLanguage || 'de',
              // ⭐ Anzahlungen der Originalrechnung NICHT übernehmen — Stornorechnungen
              // haben keine eigenen Anzahlungsabzüge. Veraltete Daten im State würden
              // sonst die negativen Positionen herausfiltern (InvoiceService:filterInvoiceItems).
              existingDownPayments: [],
              hasDownPayments: false,
              totalDownPaymentAmount: 0,
              remainingAmount: 0,
          });
          
          logger.info('Cancellation invoice data prepared successfully');
          
      } catch (error) {
          logger.error('Error loading cancellation invoice data:', error);
          throw error;
      }
  }, [currentUser, updateState, t, state.buyer, state.shipToAddress, state.paymentTerms]);

  return {
    ...state,
    setState: updateState, // Generic update function
    resetForm, // Expose the reset function
    // Specific setters
    setIsAdvancedMode,
    setInvoiceDate,
    setCurrency,
    setBuyer,
    setItems,
    setDeliveryDate,
    setUseDifferentShipTo,
    setShipToAddress,
    setPaymentTerms,
    setNotes,
    setBillingStartDate,
    setBillingEndDate,
    setTenderOrLotReference,
    setInvoicedObjectId,
    setInvoicedObjectScheme,
    setContractReference,
    setProjectReferenceId,
    setProjectReferenceName,
    setPaymentDueDate,
    setPaymentMandateReference,
    setBuyerOrderReference,
    setSellerOrderReference,
    setSelectedOfferId,
    setSelectedOfferNumber,
    setRelatedOfferData,
    setOfferWorkflowReferenceId,
    // Down payment setters
    setExistingDownPayments,
    setHasDownPayments,
    setTotalDownPaymentAmount,
    setRemainingAmount,
    setIsLoadingDownPayments,
    setShowDownPaymentSection,
    // Inline down payment creation setters
    setInlineDownPaymentType,
    setInlineDownPaymentValue,
    setInlineDownPaymentDate,
    setInlineDownPaymentNotes,
    setIsCreatingInlineDownPayment,
    // NEU: Mehrsprachigkeit
    setDocumentLanguage,
    // VB-1: Steuerart-Default
    setDefaultTaxCategorySelection,
    // Inline down payment creation handler
    handleInlineDownPaymentCreation,
    // Expose the new handler and error state
    handleOfferSelection,
    offerSelectionError,
    // Stornorechnung handler
    loadCancellationInvoiceData,
  };
}; 